import { useState, useEffect } from "react";
import { BsSearch } from "react-icons/bs";
import { MdKeyboardArrowRight } from "react-icons/md";
import { format } from "date-fns";
import DateRangeSelector from "./DateRangeSelector";
import MultiFilter from "../Dashboard/MultiFilter";
import LineChart from "./LineChart";
import PieCharts from "./PieCharts";
import {
  useFetchFiltersQuery,
  useFetchDashboardStatsMutation,
  useFetchFilterBasedOnBusinessUnitQuery,
} from "@services";
import { useTypedSelector } from "@/hooks";
function getSortedBusinessUnit(sortArray?: any[]) {
  if (!Array.isArray(sortArray)) return [];
  return [...sortArray]?.sort((a, b) => a?.label?.localeCompare(b?.label));
}

// Define the option type that matches what MultiFilter expects
type OptionType = {
  value: string;
  label: string;
};
function getSortedRegionOptions(sortArray?: any[]) {
  if (!Array.isArray(sortArray)) return [];
  return [...sortArray].sort((a, b) => a?.label?.localeCompare(b?.label));
}
function getSortedCountry(sortArray?: any[]) {
  if (!Array.isArray(sortArray)) return [];
  return [...sortArray]?.sort((a, b) => a?.label?.localeCompare(b?.label));
}
const Index = () => {
  const [businessUnit, setBusinessUnit] = useState<OptionType[]>([]);
  const [region, setRegion] = useState<OptionType[]>([]);
  const [country, setCountry] = useState<OptionType[]>([]);
  const [admin, setAdmin] = useState<OptionType[]>([]);
  const [functions, setFunction] = useState<OptionType[]>([]);
  const [grade, setGrade] = useState<OptionType[]>([]);
  const [jobOfferStatus, setJobOfferStatus] = useState<OptionType[]>([]);
  const [showPicker, setShowPicker] = useState(false);

  const [range, setRange] = useState<{
    startDate: string;
    endDate: string;
  } | null>(null);

  const { user, permissions } = useTypedSelector(state => state.auth);
  const company_id = user?.company?.id || 0;
  const skip = !user?.company?.id;

  const {
    data: filterData,
    isLoading,
    error,
  } = useFetchFiltersQuery({ company_id }, { skip });

  const {
    data: filterBasedOnBusinessUnit,
    error: filterErrorBasedOnBusinessUnit,
    isLoading: filterLoadingBasedOnBusinessUnit,
    isSuccess: filterSuccessBasedOnBusinessUnit,
  } = useFetchFilterBasedOnBusinessUnitQuery({ company_id });

  const filteredData = filterBasedOnBusinessUnit?.data?.filter((item: any) =>
    businessUnit.some(bu => bu.label === item.name)
  );

  // const myregion = filteredData?.flatMap((d: any) => d.regions);
  const myregion = filteredData
    ?.flatMap((d: any) => d.regions)
    ?.filter(
      (value: any, index: number, self: any[]) =>
        index === self.findIndex((r: any) => r.id === value.id)
    );

  // Filter regions based on selected region labels
  // const myCountry = myregion?.filter((item: any) =>
  //   region.some(r => r.label === item.name)
  // );
  // const allCountries =
  //   myCountry?.flatMap((region: any) => region.countries) || [];
  const myCountry = myregion?.filter((item: any) =>
    region.some(r => r.value === item.id.toString())
  );

  const allCountries =
    myCountry?.flatMap((region: any) => region.countries) || [];

  const [fetchDashboardStats, { data: statsData }] =
    useFetchDashboardStatsMutation();

  useEffect(() => {
    if (company_id) {
      const body = {
        statuses: jobOfferStatus.map(item => item.value.toLowerCase()),
        job_functions: functions.map(item => item.value),
        countries: country.map(item => item.value),
        grade: grade.map(item => item.label),
        business_units: businessUnit.map(item => item.value),
        regions: region.map(item => item.value),
        evaluated_by: admin.map(item => item.label.toUpperCase()),
        startDate: range?.startDate,
        endDate: range?.endDate,
      };
      fetchDashboardStats({ company_id, body });
    }
  }, [
    company_id,
    jobOfferStatus,
    functions,
    country,
    grade,
    businessUnit,
    region,
    admin,
    range,
  ]);

  // Add global ResizeObserver error handler
  useEffect(() => {
    const errorHandler = (e: ErrorEvent) => {
      if (e.message.includes("ResizeObserver")) {
        e.preventDefault();
      }
    };
    window.addEventListener("error", errorHandler);
    return () => window.removeEventListener("error", errorHandler);
  }, []);

  const formatFullDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return format(date, "EEEE, do MMMM");
  };

  // Updated dummy data to match OptionType
  const dummyChartData: OptionType[] = [
    { value: "categoryA", label: "Category A" },
    { value: "categoryB", label: "Category B" },
    { value: "categoryC", label: "Category C" },
    { value: "categoryD", label: "Category D" },
  ];

  // For charts that expect {name, value} format
  const chartData = [
    { name: "Category A", value: 40 },
    { name: "Category B", value: 25 },
    { name: "Category C", value: 20 },
    { name: "Category D", value: 15 },
  ];

  const handleBusinessUnitChange = (selected: OptionType[]) => {
    setBusinessUnit(selected);
    setRegion([]); // 🔄 Clear region
    setCountry([]); // 🔄 Clear country
  };

  const handleRegionChange = (selected: OptionType[]) => {
    setRegion(selected);
    setCountry([]); // 🔄 Clear country
  };

  const keyMap: Record<string, string> = {
    open: "In-Progress",
    close: "Evaluated",
  };

  return (
    <div className="bg-themeCore min-h-screen p-4 sm:p-5">
      <div className="top flex flex-col lg:flex-row gap-4 items-start lg:items-end">
        <div className="bg-cGreen w-full lg:w-80 flex items-center rounded-md py-5 px-3 gap-x-4">
          <div
            style={{ backgroundColor: "#A3C3F2" }}
            className="w-16 px-2 py-2 rounded-full"
          >
            <BsSearch className="text-5xl text-cGreen px-1" />
          </div>
          <div>
            <h1 className="text-white text-lg font-medium">Total Jobs</h1>
            <p className="text-white font-semibold text-2xl">
              {statsData?.data?.total}
            </p>
          </div>
        </div>

        <div className="w-full flex-1">
          <label className="text-textColor text-xs">Timeline</label>
          <button
            onClick={() => setShowPicker(true)}
            className="text-textColor flex items-center justify-between bg-white px-5 pb-3 rounded w-full"
          >
            <p className="mt-4 text-gray-700">
              {range?.startDate && range?.endDate ? (
                <>
                  {formatFullDate(range.startDate)} -{" "}
                  {formatFullDate(range.endDate)}
                </>
              ) : (
                <span>Select Date Range</span>
              )}
            </p>
            <p className="mt-4">
              <MdKeyboardArrowRight className="text-xl" />
            </p>
          </button>
        </div>
      </div>

      {/* Filters */}

      <div className="mt-5 bg-white px-5 py-6 rounded-md shadow grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <MultiFilter
          label="Business Unit"
          options={getSortedBusinessUnit(
            filterData?.data?.business_units.map((d: any) => ({
              label: d.name,
              value: d.id,
            }))
          )}
          value={businessUnit}
          onChange={handleBusinessUnitChange}
        />

        <MultiFilter
          label="Region"
          disabled={businessUnit.length === 0}
          options={getSortedRegionOptions(
            myregion?.map((d: any) => ({
              value: d.id.toString(),
              label: d.name,
              countries: d.countries,
              business_units: d.business_units,
            }))
          )}
          value={region}
          onChange={handleRegionChange}
        />

        <MultiFilter
          label="Country"
          disabled={region.length === 0} // 🔒 Disable if no Region selected
          options={getSortedCountry(
            allCountries?.map((unit: any) => ({
              label: unit.name,
              value: unit.id,
            }))
          )}
          value={country}
          onChange={setCountry}
        />

        <MultiFilter
          label="Function"
          options={filterData?.data.job_functions.map((d: any) => ({
            label: d.name,
            value: d.id,
          }))}
          value={functions}
          onChange={setFunction}
        />

        <MultiFilter
          label="Grade"
          options={filterData?.data.grades.map((grade: string) => ({
            label: grade,
            value: grade,
          }))}
          value={grade}
          onChange={setGrade}
        />

        <MultiFilter
          label="User"
          options={[{ value: "TOM", label: "Tom" }]}
          value={admin}
          onChange={setAdmin}
        />

        <MultiFilter
          label="Status"
          options={[
            { value: "Close", label: "Evaluated" },
            { value: "Open", label: "In-Progress" },
          ]}
          value={jobOfferStatus}
          onChange={setJobOfferStatus}
        />
      </div>

      {/* Charts with debounce props */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
        <LineChart
          title="Jobs by Business Unit"
          color="#1A4F9C"
          data={Object.entries(statsData?.data?.business_unit || {}).map(
            ([key, value]) => ({
              name: key,
              value: Number(value), // Ensure numeric value
            })
          )}
        />

        <LineChart
          title="Jobs by Region"
          color="#1A4F9C"
          data={Object.entries(statsData?.data?.region || {}).map(
            ([key, value]) => ({
              name: key,
              value: Number(value), // Ensure numeric value
            })
          )}
        />

        <LineChart
          title="Jobs by Country"
          color="#1A4F9C"
          data={Object.entries(statsData?.data?.country || {}).map(
            ([key, value]) => ({
              name: key,
              value: Number(value), // Ensure numeric value
            })
          )}
        />

        <LineChart
          title="Jobs by Function"
          color="#1A4F9C"
          data={Object.entries(statsData?.data?.job_function || {}).map(
            ([key, value]) => ({
              name: key,
              value: Number(value), // Ensure numeric value
            })
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
        <div className="xl:col-span-2">
          <LineChart
            title="Jobs by Grade"
            color="#1A4F9C"
            data={Object.entries(statsData?.data?.grade || {}).map(
              ([key, value]) => ({
                name: key,
                value: Number(value), // Ensure numeric value
              })
            )}
          />
        </div>
        <PieCharts
          title="Jobs by User"
          data={Object.entries(statsData?.data?.evaluated_by || {}).map(
            ([key, value]) => ({
              name: key,
              value: Number(value), // Ensure numeric value
            })
          )}
          colordata={["EF3F3F", "1A4F9C", "F48E7B", "91A3B4"]}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <PieCharts
          title="Jobs by Status"
          data={Object.entries(statsData?.data?.status || {}).map(
            ([key, value]) => ({
              name: keyMap[key] || key, // If mapping not found, fallback to original
              value: Number(value), // Ensure numeric value
            })
          )}
          colordata={["EF3F3F", "1A4F9C", "F48E7B", "91A3B4"]}
        />
      </div>

      {showPicker && (
        <div className="fixed top-0 inset-0 z-50 bg-black bg-opacity-40 flex justify-center items-center">
          <DateRangeSelector
            initialDate={range ?? undefined} // 👈 safely convert null to undefined
            onApply={selected => {
              setRange(selected);
              setShowPicker(false);
            }}
            onCancel={() => setShowPicker(false)}
          />
        </div>
      )}
    </div>
  );
};

export default Index;
